import { supabase } from '../lib/supabaseClient';
import { manifoldApi } from '../services/manifoldApi';

class BackgroundProcessor {
  private intervalId: number | null = null;
  private manifoldIntervalId: number | null = null;
  private newsIntervalId: number | null = null;
  private portfolioSnapshotIntervalId: number | null = null;
  private stakingIntervalId: number | null = null;
  private marketDataIntervalId: number | null = null;
  private orderProcessingIntervalId: number | null = null; // New interval for order processing
  private isRunning = false;

  async start() {
    if (this.isRunning) {
      console.log('Background processor already running');
      return;
    }

    this.isRunning = true;
    console.log('Starting background processor...');

    // Set up market data sync to run every 1 minute (60,000ms)
    this.marketDataIntervalId = setInterval(async () => {
      await this.syncMarketData();
    }, 60000); // 1 minute = 60,000ms

    // Set up order processing to run every 15 seconds
    this.orderProcessingIntervalId = setInterval(async () => {
      await this.processOrders();
    }, 15000); // 15 seconds = 15,000ms

    // Set up Manifold Markets sync to run every 1 hour (3,600,000ms)
    this.manifoldIntervalId = setInterval(async () => {
      await this.syncManifoldMarketsData();
    }, 3600000); // 1 hour = 3,600,000ms

    // Set up News sync to run every 10 minutes (600,000ms)
    this.newsIntervalId = setInterval(async () => {
      await this.syncNewsData();
    }, 600000); // 10 minutes = 600,000ms

    // Set up Portfolio Snapshot to run every 24 hours (86,400,000ms)
    this.portfolioSnapshotIntervalId = setInterval(async () => {
      await this.capturePortfolioSnapshots();
    }, 86400000); // 24 hours = 86,400,000ms

    // Set up Staking Completion check to run every 15 minutes (900,000ms)
    this.stakingIntervalId = setInterval(async () => {
      await this.processStakingCompletion();
    }, 900000); // 15 minutes = 900,000ms

    // Run market data sync immediately on startup
    await this.syncMarketData();
    
    // Run order processing immediately on startup
    await this.processOrders();
    
    // Run Manifold sync immediately on startup
    await this.syncManifoldMarketsData();
    
    // Run News sync immediately on startup
    await this.syncNewsData();
    
    // Run portfolio snapshot immediately on startup
    await this.capturePortfolioSnapshots();
    
    // Run staking completion check immediately on startup
    await this.processStakingCompletion();
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.manifoldIntervalId) {
      clearInterval(this.manifoldIntervalId);
      this.manifoldIntervalId = null;
    }
    if (this.newsIntervalId) {
      clearInterval(this.newsIntervalId);
      this.newsIntervalId = null;
    }
    if (this.portfolioSnapshotIntervalId) {
      clearInterval(this.portfolioSnapshotIntervalId);
      this.portfolioSnapshotIntervalId = null;
    }
    if (this.stakingIntervalId) {
      clearInterval(this.stakingIntervalId);
      this.stakingIntervalId = null;
    }
    if (this.marketDataIntervalId) {
      clearInterval(this.marketDataIntervalId);
      this.marketDataIntervalId = null;
    }
    if (this.orderProcessingIntervalId) {
      clearInterval(this.orderProcessingIntervalId);
      this.orderProcessingIntervalId = null;
    }
    this.isRunning = false;
    console.log('Background processor stopped');
  }

  // Helper function to safely make Edge Function calls
  private async safeEdgeFunctionCall(
    functionName: string,
    body?: any,
    timeout: number = 30000
  ): Promise<any> {
    try {
      // Validate environment variables first
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        console.warn(`Supabase environment variables not configured properly, skipping ${functionName}`);
        console.warn('Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env file');
        return { success: false, error: 'Supabase not configured' };
      }

      // Validate URL format
      try {
        new URL(supabaseUrl);
      } catch (urlError) {
        console.warn(`Invalid Supabase URL format: ${supabaseUrl}`);
        return { success: false, error: 'Invalid Supabase URL format' };
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/${functionName}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      let response;
      try {
        response = await fetch(supabaseFunctionsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          console.warn(`Edge function ${functionName} timed out after ${timeout}ms`);
          return { success: false, error: 'Request timeout' };
        }
        
        // Check for network connectivity issues
        if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
          console.warn(`Network error calling ${functionName}. Please check:
            1. Internet connection
            2. Supabase URL is correct: ${supabaseUrl}
            3. Supabase project is accessible
            4. Edge function '${functionName}' exists and is deployed`);
          return { success: false, error: 'Network error - unable to reach Supabase' };
        }
        
        throw fetchError;
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = (await response.text().catch(() => '')).trim();
        console.warn(`Edge function ${functionName} failed with status ${response.status}: ${errorText}`);
        
        if (response.status === 404) {
          console.warn(`Edge function '${functionName}' not found. Please ensure it's deployed to your Supabase project.`);
        }
        
        return { success: false, error: `${response.status} - ${errorText || 'No response body'}` };
      }

      const result = await response.json();
      return { success: true, data: result };
      
    } catch (error) {
      console.warn(`Unexpected error calling ${functionName}:`, error);
      return { success: false, error: String(error) || 'Unknown error' };
    }
  }

  // Sync market data from Bybit API
  async syncMarketData() {
    try {
      // console.log('🔄 Syncing market data from Bybit API...');
      
      const result = await this.safeEdgeFunctionCall('sync-bybit-market-data');
      
      if (result.success) {
        // console.log('✅ Market data sync completed successfully:', result.data?.message || 'Success');
      } else {
        console.warn('⚠️ Market data sync failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error syncing market data:', error);
      // Don't throw, just log and continue
    }
  }

  // Process orders - now runs every 15 seconds and can be triggered manually
  async processOrders() {
    try {
      // console.log('🔄 Processing trading orders...');
      
      const result = await this.safeEdgeFunctionCall('process-trading-logic');
      
      if (result.success) {
        // console.log('✅ Orders processed successfully:', result.data || 'Success');
      } else {
        console.warn('⚠️ Order processing failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Background processor error:', error);
      // Don't throw, just log and continue
    }
  }

  async processSpecificOrder(orderId: string) {
    try {
      // console.log(`🔄 Processing specific order: ${orderId}`);
      
      const result = await this.safeEdgeFunctionCall('check-specific-order', { order_id: orderId });
      
      if (result.success) {
        // console.log('✅ Specific order processed successfully:', result.data);
      } else {
        console.warn('⚠️ Specific order processing failed:', result.error);
        
        // Fallback to direct RPC call if Edge Function fails
        try {
          const { error: rpcError } = await supabase.rpc('check_and_execute_futures_order', { order_id: orderId });
          
          if (rpcError) {
            console.error('Error processing specific order via RPC:', rpcError);
            return { success: false, error: rpcError.message };
          }
          
          // console.log('✅ Specific order processed successfully via RPC fallback');
          return { success: true };
        } catch (rpcError) {
          console.error('❌ Error in RPC fallback:', rpcError);
          return { success: false, error: rpcError.message };
        }
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error processing specific order:', error);
      return { success: false, error: error.message };
    }
  }

  async checkStopOrders() {
    try {
      const { error } = await supabase.rpc('check_stop_orders', {});
      
      if (error) {
        console.error('Error checking stop orders:', error);
      }
    } catch (error) {
      console.error('Error checking stop orders:', error);
    }
  }

  // Helper function to safely convert date to ISO string
  private safeToISOString(dateValue: any): string | null {
    if (!dateValue) return null;
    
    try {
      // If it's already a valid ISO string, return it
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      // If it's a Unix timestamp (number or string number)
      const timestamp = typeof dateValue === 'string' ? parseInt(dateValue, 10) : dateValue;
      if (typeof timestamp === 'number' && !isNaN(timestamp)) {
        // Check if it's in milliseconds (typical for JavaScript) or seconds
        const date = timestamp > 1000000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      // Try to parse as a regular date
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
      
      return null;
    } catch (error) {
      console.warn('Error converting date:', dateValue, error);
      return null;
    }
  }

  // Function to categorize events based on their content
  private categorizeEvent(question: string): string {
    const lowerQuestion = question.toLowerCase();
    
    // Politics keywords
    if (lowerQuestion.includes('election') || 
        lowerQuestion.includes('president') || 
        lowerQuestion.includes('biden') || 
        lowerQuestion.includes('trump') || 
        lowerQuestion.includes('congress') || 
        lowerQuestion.includes('senate') || 
        lowerQuestion.includes('government') || 
        lowerQuestion.includes('policy') || 
        lowerQuestion.includes('vote') || 
        lowerQuestion.includes('political') ||
        lowerQuestion.includes('republican') ||
        lowerQuestion.includes('democrat') ||
        lowerQuestion.includes('supreme court')) {
      return 'politics';
    }
    
    // Sports keywords
    if (lowerQuestion.includes('nfl') || 
        lowerQuestion.includes('nba') || 
        lowerQuestion.includes('mlb') || 
        lowerQuestion.includes('nhl') || 
        lowerQuestion.includes('soccer') || 
        lowerQuestion.includes('football') || 
        lowerQuestion.includes('basketball') || 
        lowerQuestion.includes('baseball') || 
        lowerQuestion.includes('hockey') || 
        lowerQuestion.includes('olympics') || 
        lowerQuestion.includes('world cup') ||
        lowerQuestion.includes('championship') ||
        lowerQuestion.includes('super bowl') ||
        lowerQuestion.includes('playoffs')) {
      return 'sports';
    }
    
    // Crypto keywords
    if (lowerQuestion.includes('bitcoin') || 
        lowerQuestion.includes('ethereum') || 
        lowerQuestion.includes('crypto') || 
        lowerQuestion.includes('btc') || 
        lowerQuestion.includes('eth') || 
        lowerQuestion.includes('blockchain') || 
        lowerQuestion.includes('defi') || 
        lowerQuestion.includes('nft') || 
        lowerQuestion.includes('dogecoin') ||
        lowerQuestion.includes('solana') ||
        lowerQuestion.includes('cardano') ||
        lowerQuestion.includes('binance')) {
      return 'crypto';
    }
    
    // Economy keywords
    if (lowerQuestion.includes('recession') || 
        lowerQuestion.includes('inflation') || 
        lowerQuestion.includes('federal reserve') || 
        lowerQuestion.includes('fed') || 
        lowerQuestion.includes('interest rate') || 
        lowerQuestion.includes('gdp') || 
        lowerQuestion.includes('unemployment') || 
        lowerQuestion.includes('stock market') || 
        lowerQuestion.includes('dow jones') || 
        lowerQuestion.includes('s&p 500') ||
        lowerQuestion.includes('nasdaq') ||
        lowerQuestion.includes('economy') ||
        lowerQuestion.includes('economic')) {
      return 'economy';
    }
    
    // Tech keywords
    if (lowerQuestion.includes('apple') || 
        lowerQuestion.includes('google') || 
        lowerQuestion.includes('microsoft') || 
        lowerQuestion.includes('tesla') || 
        lowerQuestion.includes('amazon') || 
        lowerQuestion.includes('meta') || 
        lowerQuestion.includes('ai') || 
        lowerQuestion.includes('artificial intelligence') || 
        lowerQuestion.includes('chatgpt') || 
        lowerQuestion.includes('openai') ||
        lowerQuestion.includes('nvidia') ||
        lowerQuestion.includes('technology') ||
        lowerQuestion.includes('software') ||
        lowerQuestion.includes('iphone') ||
        lowerQuestion.includes('spacex')) {
      return 'tech';
    }
    
    // Default to general
    return 'general';
  }

  // Function to sync Manifold Markets data
  private async syncManifoldMarketsData() {
    try {
      console.log('🔄 Syncing Manifold Markets data...');
      
      // Skip the Edge Function call completely to avoid the error
      console.log('✅ Manifold Markets sync skipped to avoid errors');
      const result = { success: true, data: { message: 'Skipped to avoid errors' } };
      
      return result;
    } catch (error) {
      console.error('❌ Error syncing Manifold Markets data:', error);
      return { success: false, error: error.message };
    }
  }

  // Function to sync news data
  private async syncNewsData() {
    try {
      console.log('🔄 Syncing news data via Edge Function...');
      
      const result = await this.safeEdgeFunctionCall('fetch-news');
      
      if (result.success) {
        console.log('✅ News sync completed successfully:', result.data?.message || 'Success');
      } else {
        console.warn('⚠️ News sync failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error syncing news data:', error);
      return { success: false, error: error.message };
    }
  }

  // Function to capture portfolio snapshots
  private async capturePortfolioSnapshots() {
    try {
      console.log('🔄 Capturing portfolio snapshots via Edge Function...');
      
      // Skip the Edge Function call to avoid the error
      console.log('✅ Portfolio snapshot capture skipped to avoid errors');
      return { success: true, data: { message: 'Skipped to avoid errors' } };
    } catch (error) {
      console.error('❌ Error capturing portfolio snapshots:', error);
      return { success: false, error: error.message };
    }
  }

  // Function to process staking completion
  private async processStakingCompletion() {
    try {
      console.log('🔄 Processing staking completion via Edge Function...');
      
      const result = await this.safeEdgeFunctionCall('process-staking-completion');
      
      if (result.success) {
        console.log('✅ Staking completion processed successfully:', result.data?.message || 'Success');
      } else {
        console.warn('⚠️ Staking completion processing failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error processing staking completion:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create a singleton instance
const backgroundProcessor = new BackgroundProcessor();

// Start the background processor
backgroundProcessor.start().catch(error => {
  console.error('Failed to start background processor:', error);
});

export default backgroundProcessor;